import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

// The list pages' shared controls (Organizaciones, Contactos): the "Filtros"
// button with its counter, and the "Columnas" menu.

// "Filtros": opens the extra filters; marked and counted while some are on.
//   cuenta: how many of them are on; controla: the id of the panel it opens
export function BotonFiltros({ abierto, cuenta, onClick, controla }) {
  const { t } = useTranslation('comercial')
  return (
    <button type="button" onClick={onClick} aria-expanded={abierto} aria-controls={controla}
      aria-label={cuenta ? `${t('organizaciones.filtros')} (${t('organizaciones.filtrosAplicados', { count: cuenta })})` : undefined}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm ${abierto || cuenta
        ? 'border-gray-400 bg-gray-50 text-gray-900' : 'border-gray-200 bg-white text-gray-600 hover:text-gray-900'}`}>
      <IconoFiltros />
      {t('organizaciones.filtros')}
      {cuenta > 0 && (
        <span className="min-w-[1.25rem] h-5 px-1.5 rounded-full bg-gray-900 text-white text-xs font-medium inline-flex items-center justify-center tabular-nums">
          {cuenta}
        </span>
      )}
    </button>
  )
}

// "Columnas": which of the optional columns the table shows. The fixed one is
// listed checked and disabled; "Restablecer" goes back to the default.
//   fija: the fixed column's title; opcionales: [key]; titulos: { key: title }
export function MenuColumnas({ columnas, fija, opcionales, predeterminadas, titulos, onCambiar, alternar }) {
  const { t } = useTranslation('comercial')
  const [abierto, setAbierto] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!abierto) return undefined
    const fuera = (e) => { if (!ref.current?.contains(e.target)) setAbierto(false) }
    const esc = (e) => { if (e.key === 'Escape') setAbierto(false) }
    document.addEventListener('mousedown', fuera)
    document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('keydown', esc) }
  }, [abierto])
  const sonPredeterminadas = columnas.length === predeterminadas.length && predeterminadas.every(c => columnas.includes(c))
  const opcion = 'flex items-center gap-2 px-3 py-1.5 text-sm'
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setAbierto(a => !a)} aria-haspopup="true" aria-expanded={abierto}
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 hover:text-gray-900">
        <IconoColumnas />
        {t('organizaciones.columnas')}
      </button>
      {abierto && (
        <div role="group" aria-label={t('organizaciones.columnas')}
          className="absolute right-0 top-full mt-1 z-20 w-60 rounded-lg border border-gray-200 bg-white shadow-lg py-1">
          <label className={`${opcion} text-gray-400`}>
            <input type="checkbox" checked disabled className="rounded border-gray-300" />
            <span>{fija} <span className="text-xs">· {t('organizaciones.columnaFija')}</span></span>
          </label>
          {opcionales.map(c => (
            <label key={c} className={`${opcion} text-gray-700 hover:bg-gray-50 cursor-pointer`}>
              <input type="checkbox" checked={columnas.includes(c)} onChange={() => onCambiar(alternar(columnas, c))}
                className="rounded border-gray-300" />
              {titulos[c]}
            </label>
          ))}
          <div className="border-t border-gray-100 mt-1 pt-1">
            <button type="button" disabled={sonPredeterminadas} onClick={() => onCambiar([...predeterminadas])}
              className="block w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:text-gray-300 disabled:hover:bg-transparent">
              {t('organizaciones.restablecerColumnas')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function IconoFiltros() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true" className="w-4 h-4">
      <path d="M3 5h14M6 10h8M8.5 15h3" />
    </svg>
  )
}

function IconoColumnas() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true" className="w-4 h-4">
      <rect x="3" y="4" width="14" height="12" rx="1.5" />
      <path d="M8 4v12M12.5 4v12" />
    </svg>
  )
}
